import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  isTokenValid, 
  redirectToLogin,
  authenticatedFetch,
  getBaseUrl
} from '../utils/tokenUtils';
import UpgradeModal from '../components/UpgradeModal';
import FeedbackSupportModal from '../components/FeedbackSupportModal';
import ShortLinkEditModal from '../components/ShortLinkEditModal';
import ShortLinkInfo from '../components/ShortLinkInfo';
import ShortLinkStats from '../components/ShortLinkStats';
import ShortLinkAnalytics from '../components/ShortLinkAnalytics';

const ShortLinkDetail = ({ user, onLogout }) => {
  const { shortLinkId } = useParams();
  const navigate = useNavigate();
  const [shortLink, setShortLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [copied, setCopied] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingShortLink, setEditingShortLink] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const handleUpgradeClick = () => {
    if (user?.subscription?.plan === 'enterprise') {
      return;
    }
    setShowUpgradeModal(true);
  };

  const handleUpgrade = async (paymentData) => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      const response = await authenticatedFetch('/api/dashboard/user/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData)
      });

      if (response.ok) {
        const result = await response.json();
        showToast('Subscription upgraded successfully!', 'success');
        setShowUpgradeModal(false);
        window.location.reload();
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Subscription upgrade failed.', 'error');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      showToast('An error occurred during subscription upgrade.', 'error');
    }
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      const response = await authenticatedFetch('/api/contact', {
        method: 'POST',
        body: feedbackData
      });

      if (response.ok) {
        showToast('Feedback submitted successfully!', 'success');
      } else {
        showToast('Failed to submit feedback. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showToast('Failed to submit feedback. Please try again.', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    // Toast implementation would go here
    console.log(`${type}: ${message}`);
  };

  const fetchShortLinkDetails = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      setLoading(true);
      console.log('Fetching short link details for ID:', shortLinkId);
      
      // Fetch both short link details and analytics data
      const [shortLinkResponse, analyticsResponse] = await Promise.all([
        authenticatedFetch(`/api/shortlink/${shortLinkId}`),
        authenticatedFetch(`/api/shortlink/${shortLinkId}/analytics/detailed?period=${selectedPeriod}&platform=${selectedPlatform}`)
      ]);

      console.log('ShortLink API response status:', shortLinkResponse.status);
      console.log('Analytics API response status:', analyticsResponse.status);

      if (!shortLinkResponse.ok) {
        const errorText = await shortLinkResponse.text();
        console.error('ShortLink API error response:', errorText);
        throw new Error(`ShortLink API error: ${shortLinkResponse.status} - ${errorText}`);
      }

      if (!analyticsResponse.ok) {
        const errorText = await analyticsResponse.text();
        console.error('Analytics API error response:', errorText);
        throw new Error(`Analytics API error: ${analyticsResponse.status} - ${errorText}`);
      }

      const shortLinkData = await shortLinkResponse.json();
      const analyticsData = await analyticsResponse.json();
      
      console.log('ShortLink API response data:', shortLinkData);
      console.log('Analytics API response data:', analyticsData);

      // Set the complete short link data
      setShortLink(shortLinkData);

      // Transform the API data to match component expectations
      const transformedAnalytics = {
        shortLink: {
          shortCode: analyticsData.shortLink?.shortCode,
          title: analyticsData.shortLink?.title,
          projectLogo: analyticsData.shortLink?.projectLogo,
          createdAt: analyticsData.shortLink?.createdAt
        },
        totalClicks: analyticsData.summary?.totalClicks || 0,
        uniqueClicks: analyticsData.summary?.uniqueClicks || 0,
        dailyAverage: analyticsData.summary?.averageClicksPerDay || 0,
        countries: analyticsData.countries || [],
        chartData: analyticsData.timeSeries || [],
        platforms: (analyticsData.platforms || []).map(item => ({
          platform: item.platform,
          count: item.count
        })),
        browsers: (analyticsData.browsers || []).map(item => ({
          browser: item.browser,
          count: item.count
        })),
        referrers: analyticsData.referrers || []
      };
      
      console.log('Using real analytics data:', transformedAnalytics);
      setAnalytics(transformedAnalytics);

    } catch (error) {
      console.error('Error fetching short link details:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else {
        showToast('Error loading data: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch('/api/dashboard/user/projects');
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      }
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${getBaseUrl()}/s/${analytics?.shortLink?.shortCode}`);
      setCopied(true);
      showToast('Link copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      showToast('Failed to copy link', 'error');
    }
  };

  const handleEdit = () => {
    setEditingShortLink(shortLink);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!shortLink) {
      showToast('Short link not found', 'error');
      return;
    }

    const shortLinkId = shortLink._id || shortLink.id;
    if (!shortLinkId) {
      showToast('Short link ID not found', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this short link? This action cannot be undone.')) {
      return;
    }

    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch(`/api/shortlink/${shortLinkId}`, {
        method: 'DELETE'
      });

      showToast('Short link deleted successfully!', 'success');
      setTimeout(() => {
        navigate('/dashboard/shortlinks');
      }, 1000);
    } catch (error) {
      console.error('Delete short link error:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else {
        showToast('Error occurred while deleting short link.', 'error');
      }
    }
  };

  const handleOpenLink = () => {
    window.open(`${getBaseUrl()}/s/${analytics?.shortLink?.shortCode}`, '_blank');
  };

  useEffect(() => {
    if (shortLinkId) {
      fetchShortLinkDetails();
      fetchProjects();
    }
  }, [shortLinkId, selectedPeriod, selectedPlatform]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!shortLink || !analytics) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Short Link Not Found</h2>
          <p className="text-gray-400 mb-6">The requested short link could not be found or you don't have permission to view it.</p>
          <p className="text-gray-500 mb-6">Short Link ID: {shortLinkId}</p>
          <button
            onClick={() => navigate('/dashboard/shortlinks')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Short Links
          </button>
        </div>
      </div>
    );
  }

  return (
                  <div>
          <div className="max-w-7xl mx-auto p-6">
            {/* Link Info */}
        <ShortLinkInfo
          analytics={analytics}
          copied={copied}
          onCopy={handleCopy}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onOpenLink={handleOpenLink}
        />

        {/* Stats Cards */}
        <ShortLinkStats analytics={analytics} />

        {/* Analytics */}
        <ShortLinkAnalytics
          analytics={analytics}
          selectedPeriod={selectedPeriod}
          selectedPlatform={selectedPlatform}
          onPeriodChange={setSelectedPeriod}
          onPlatformChange={setSelectedPlatform}
          user={user}
          />
        </div>

      {/* Modals */}
              <ShortLinkEditModal
                isOpen={showForm}
                onClose={() => {
                  setShowForm(false);
                  setEditingShortLink(null);
                }}
                shortLink={editingShortLink}
                projects={projects}
                onSuccess={fetchShortLinkDetails}
              />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={user}
        onUpgrade={handleUpgrade}
      />

      <FeedbackSupportModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        user={user}
      />
    </div>
  );
};

  export default ShortLinkDetail;